import { useMemo } from "react";
import { Card, BlockStack, Text, Bleed } from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { GenerativeTextField } from "@shared/ui";
import { EventSlugField } from "@shared/ui";
import { MultiSelectCombobox } from "@shared/ui";
import useLanguages from "@shared/hooks/common/useLanguages";
import { Brand } from "@features/events/types";

interface OverviewCardProps {
  brandData: Partial<Brand>;
  fieldErrors: Record<string, string>;
  hcc: string | undefined;
  onDataChange: (updates: Partial<Brand>) => void;
}

export default function OverviewCard({
  brandData,
  fieldErrors,
  hcc,
  onDataChange,
}: OverviewCardProps) {
  const { t } = useTranslation();
  const { languages, isLoading: isLoadingLanguages } = useLanguages();

  // Get available languages from useLanguages hook
  const languageOptions = useMemo(() => {
    return languages.map((language) => ({
      label: language.englishLabel,
      value: language.iso,
    }));
  }, [languages]);

  return (
    <Card>
      <BlockStack gap="400">
        <Text as="h2" variant="headingMd">
          {t('brandProfile.overview.title')}
        </Text>

        <GenerativeTextField
          id="name"
          placementId="event.brand.name"
          label={t('brandProfile.overview.name')}
          value={brandData.name || {}}
          onChange={(value) => onDataChange({ name: value })}
          requiredIndicator
          error={fieldErrors.name}
          type="textField"
          canGenerate={false}
        />

        <GenerativeTextField
          id="headline"
          placementId="event.brand.headline"
          label={t('brandProfile.overview.headline')}
          value={brandData.headline || {}}
          onChange={(value) => onDataChange({ headline: value })}
          requiredIndicator
          error={fieldErrors.headline}
          type="textField"
          canGenerate={true}
        />

        <GenerativeTextField
          id="description"
          placementId="event.brand.description"
          label={t('brandProfile.overview.description')}
          value={brandData.description || {}}
          onChange={(value) => onDataChange({ description: value })}
          requiredIndicator
          error={fieldErrors.description}
          type="textArea"
          canGenerate={true}
        />

        <EventSlugField
          value={brandData.slug || ""}
          onChange={(value) => onDataChange({ slug: value })}
          healthcareCompanyId={hcc || null}
          entityId={brandData.id}
          entityType="brand"
          requiredIndicator
        />

        {/* Languages Section */}
        <BlockStack gap="100">
          <Text variant="bodyMd" as="p" requiredIndicator>
            {t("brandProfile.languages.select.title")}
          </Text>
          {isLoadingLanguages ? (
            <Text as="p" variant="bodySm" tone="subdued">
              {t("common.loading")}
            </Text>
          ) : (
            <MultiSelectCombobox
              options={languageOptions}
              selected={brandData.languageCodes || []}
              onChange={(selectedLanguages: string[]) => onDataChange({ languageCodes: selectedLanguages })}
              placeholder={t("brandProfile.languages.select.placeholder")}
              error={fieldErrors.languageCodes}
            />
          )}
          <Bleed marginBlockStart="050">
            <Text as="p" variant="bodySm" tone="subdued">
              {t("brandProfile.languages.select.helpText")}
            </Text>
          </Bleed>
        </BlockStack>
      </BlockStack>
    </Card>
  );
}
